/**
 * BP-026 · Platform audit log with filters.
 */
import { useEffect, useState } from "react";
import { ScrollText, Filter } from "lucide-react";
import { apiGet } from "@/lib/api";

const CODE_COLOR = (c) => {
  if (c.includes("SUSPEND") || c.includes("REVOK") || c.includes("ARCHIVE"))
    return "bg-red-500/20 text-red-300 border-red-500/40";
  if (c.includes("BOOTSTRAP") || c.includes("GRANT"))
    return "bg-emerald-50 text-emerald-700 border-emerald-400";
  if (c.includes("IMPERSON"))
    return "bg-purple-500/20 text-purple-300 border-purple-500/40";
  if (c.includes("FLAG") || c.includes("SETTING"))
    return "bg-blue-500/20 text-blue-300 border-blue-500/40";
  return "bg-slate-500/20 text-slate-700 border-slate-500/40";
};

export default function PlatformAudit() {
  const [rows, setRows] = useState([]);
  const [codes, setCodes] = useState([]);
  const [filter, setFilter] = useState({ event_code: "", actor_id: "" });
  const [summary, setSummary] = useState({});

  const load = async () => {
    const qs = new URLSearchParams();
    if (filter.event_code) qs.set("event_code", filter.event_code);
    if (filter.actor_id) qs.set("actor_id", filter.actor_id);
    qs.set("limit", "100");
    const r = await apiGet(`/api/v3/platform/audit-events?${qs}`);
    setRows(r.events || []);
    setCodes(r.known_codes || []);
    const s = await apiGet("/api/v3/platform/audit-events/summary?days=7");
    setSummary(s.by_event_code || {});
  };
  useEffect(() => {
    load();
  }, []);

  return (
    <div data-testid="platform-audit" className="p-8 space-y-6">
      <header>
        <div className="text-xs uppercase text-slate-500 flex items-center gap-1">
          <ScrollText className="w-4 h-4" />
          Immutable audit trail
        </div>
        <h1 className="text-2xl font-bold">Audit log</h1>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {Object.entries(summary).slice(0, 8).map(([code, count]) => (
          <div
            key={code}
            data-testid={`audit-summary-${code}`}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2"
          >
            <div className="text-[10px] uppercase text-slate-500">{code}</div>
            <div className="text-lg font-semibold text-slate-900">{count}</div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
          <Filter className="w-3 h-3" />
          Filters
        </div>
        <div className="flex gap-2 flex-wrap">
          <select
            data-testid="audit-filter-code"
            value={filter.event_code}
            onChange={(e) => setFilter((f) => ({ ...f, event_code: e.target.value }))}
            className="bg-white border border-slate-300 rounded-md px-2 py-1 text-sm text-slate-800"
          >
            <option value="">All event codes</option>
            {codes.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <input
            data-testid="audit-filter-actor"
            value={filter.actor_id}
            onChange={(e) => setFilter((f) => ({ ...f, actor_id: e.target.value }))}
            placeholder="Actor id"
            className="bg-white border border-slate-300 rounded-md px-2 py-1 text-sm text-slate-800"
          />
          <button
            data-testid="audit-filter-apply"
            onClick={load}
            className="rounded-md bg-slate-100 border border-slate-300 px-3 py-1 text-sm hover:bg-slate-700"
          >
            Apply
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2">When</th>
              <th className="px-4 py-2">Event</th>
              <th className="px-4 py-2">Actor</th>
              <th className="px-4 py-2">Target</th>
              <th className="px-4 py-2">IP</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {rows.map((e, i) => (
              <tr key={i} data-testid={`audit-row-${i}`}>
                <td className="px-4 py-2 text-slate-600 whitespace-nowrap">
                  {e.created_at?.replace("T", " ").slice(0, 19)}
                </td>
                <td className="px-4 py-2">
                  <span
                    className={`rounded-full border px-2 py-0.5 text-[11px] ${CODE_COLOR(e.event_code)}`}
                  >
                    {e.event_code}
                  </span>
                </td>
                <td className="px-4 py-2 text-slate-700 truncate max-w-[220px]">
                  {e.actor_email || e.actor_id}
                </td>
                <td className="px-4 py-2 text-slate-600 truncate max-w-[220px]">
                  {e.target_type ? `${e.target_type}:${e.target_id}` : "—"}
                </td>
                <td className="px-4 py-2 text-slate-500">{e.ip || "—"}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                  No events match.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
